import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@shared/database.types";

type Quote = Database["public"]["Tables"]["quotes"]["Row"];
type QuoteInsert = Database["public"]["Tables"]["quotes"]["Insert"];
type QuoteUpdate = Database["public"]["Tables"]["quotes"]["Update"];
export type QuoteStatus = Database["public"]["Enums"]["quote_status"];

export type QuoteWithRelations = Quote & {
  client: { name: string } | null;
  referral_partner: { name: string } | null;
};

const QUOTES_BUCKET = "quotes";

export function useQuotes() {
  const { agencyId } = useAuth();

  return useQuery({
    queryKey: ["quotes", agencyId],
    enabled: !!agencyId,
    queryFn: async (): Promise<QuoteWithRelations[]> => {
      const { data, error } = await supabase
        .from("quotes")
        .select("*, client:clients(name), referral_partner:referral_partners(name)")
        .eq("agency_id", agencyId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as QuoteWithRelations[];
    },
  });
}

/** Mini-hook per il select "Referente" nel form preventivo */
export function useReferralPartners() {
  const { agencyId } = useAuth();

  return useQuery({
    queryKey: ["referral-partners", agencyId],
    enabled: !!agencyId,
    queryFn: async (): Promise<Array<{ id: string; name: string }>> => {
      const { data, error } = await supabase
        .from("referral_partners")
        .select("id, name")
        .eq("agency_id", agencyId!)
        .eq("active", true)
        .order("name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Carica il PDF nel bucket privato `quotes` e ritorna lo storage path. */
export async function uploadQuoteFile(clientId: string, file: File): Promise<string> {
  const safeName = file.name.replace(/[^\w.-]/g, "_");
  const path = `${clientId}/${Date.now()}-${safeName}`;
  const { error } = await supabase.storage
    .from(QUOTES_BUCKET)
    .upload(path, file, { upsert: false });
  if (error) throw error;
  return path;
}

/** Signed URL temporanea per scaricare il PDF (bucket privato). */
export async function getQuoteFileUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(QUOTES_BUCKET)
    .createSignedUrl(path, 60 * 60);
  if (error) return null;
  return data.signedUrl;
}

export function useCreateQuote() {
  const qc = useQueryClient();
  const { agencyId, user } = useAuth();

  return useMutation({
    mutationFn: async (
      input: Omit<
        QuoteInsert,
        "agency_id" | "created_by" | "file_path" | "proposal_file_path" | "contract_file_path"
      > & {
        /** PDF preventivo */
        file?: File | null;
        /** Proposta di valore (PDF) */
        proposalFile?: File | null;
        /** Contratto (PDF) */
        contractFile?: File | null;
      },
    ) => {
      if (!agencyId || !user) throw new Error("Auth mancante");
      const { file, proposalFile, contractFile, ...fields } = input;

      const uploaded: string[] = [];
      const uploadOne = async (f: File | null | undefined): Promise<string | null> => {
        if (!f) return null;
        const path = await uploadQuoteFile(fields.client_id, f);
        uploaded.push(path);
        return path;
      };

      try {
        const filePath = await uploadOne(file);
        const proposalPath = await uploadOne(proposalFile);
        const contractPath = await uploadOne(contractFile);

        const { data, error } = await supabase
          .from("quotes")
          .insert({
            ...fields,
            agency_id: agencyId,
            created_by: user.id,
            file_path: filePath,
            proposal_file_path: proposalPath,
            contract_file_path: contractPath,
          })
          .select()
          .single();
        if (error) throw error;
        return data as Quote;
      } catch (e) {
        // cleanup dei file orfani se upload o insert falliscono
        if (uploaded.length) {
          await supabase.storage.from(QUOTES_BUCKET).remove(uploaded);
        }
        throw e;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}

export function useUpdateQuote() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: QuoteUpdate;
      /** Nuovo PDF preventivo (sostituisce l'eventuale precedente) */
      file?: File | null;
      /** Nuova proposta di valore (sostituisce l'eventuale precedente) */
      proposalFile?: File | null;
      /** Nuovo contratto (sostituisce l'eventuale precedente) */
      contractFile?: File | null;
      /** client_id del preventivo, necessario per il path di upload */
      clientId?: string;
      /** path precedenti, rimossi best-effort dopo la sostituzione */
      previousFilePath?: string | null;
      previousProposalFilePath?: string | null;
      previousContractFilePath?: string | null;
    }) => {
      let patch = input.patch;
      const uploadedPaths: string[] = [];
      const replacedPaths: string[] = [];

      try {
        if (input.file && input.clientId) {
          const path = await uploadQuoteFile(input.clientId, input.file);
          uploadedPaths.push(path);
          patch = { ...patch, file_path: path };
          if (input.previousFilePath) replacedPaths.push(input.previousFilePath);
        }
        if (input.proposalFile && input.clientId) {
          const path = await uploadQuoteFile(input.clientId, input.proposalFile);
          uploadedPaths.push(path);
          patch = { ...patch, proposal_file_path: path };
          if (input.previousProposalFilePath) {
            replacedPaths.push(input.previousProposalFilePath);
          }
        }
        if (input.contractFile && input.clientId) {
          const path = await uploadQuoteFile(input.clientId, input.contractFile);
          uploadedPaths.push(path);
          patch = { ...patch, contract_file_path: path };
          if (input.previousContractFilePath) {
            replacedPaths.push(input.previousContractFilePath);
          }
        }

        const { data, error } = await supabase
          .from("quotes")
          .update(patch)
          .eq("id", input.id)
          .select()
          .single();
        if (error) throw error;

        // best-effort: rimuovi i vecchi file sostituiti
        if (replacedPaths.length) {
          await supabase.storage.from(QUOTES_BUCKET).remove(replacedPaths);
        }
        return data as Quote;
      } catch (e) {
        // cleanup dei nuovi file se upload successivi o update falliscono
        if (uploadedPaths.length) {
          await supabase.storage.from(QUOTES_BUCKET).remove(uploadedPaths);
        }
        throw e;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      quote: Pick<Quote, "id" | "file_path" | "proposal_file_path" | "contract_file_path">,
    ) => {
      const paths = [
        quote.file_path,
        quote.proposal_file_path,
        quote.contract_file_path,
      ].filter((p): p is string => !!p);
      if (paths.length) {
        // best-effort: la row va eliminata anche se i file mancano
        await supabase.storage.from(QUOTES_BUCKET).remove(paths);
      }
      const { error } = await supabase.from("quotes").delete().eq("id", quote.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}
